import styled from "styled-components";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Observer} from "mobx-react";
import {useAppStore} from "../../../../../StateManager";
import {IconButtonV5} from "../../../interaction/IconButtonV5";
import GoBackIcon from "../../../icons/arrow-narrow-left.svg?react";
import GoogleIcon from "../../../icons/google-icon.svg?react";
import {XHRLoader} from "../../../../../utils/loader/XHRLoader";
import {ImageUtils} from "../../../../../utils/image/ImageUtils";
import {colorPalette} from "../../../styles/colorPalette";
import {baseDistance, FlexCenter, FlexCenterStyle, fontWeight, VerticalFlex, VerticalFlexStyle} from "../../../styles/styles";
import {openKeywordsPopupForFile} from "../../../../modules/catalog/create/CatalogUtils";
import {ButtonStyled, ButtonV5} from "../../../button/ButtonV5";
import {DomPortal} from "../../../../modules/abstract/portal/DomPortal";
import {SearchFieldV5} from "../../../input/search/SearchFieldV5";
import type {LibraryItem} from "./LibraryItemsV5";

interface IGoogleImagePanelProps {
	readonly defaultSearchValue: string;
	readonly addedItems: LibraryItem[];
	readonly onCloseClick: () => void;
	readonly addImage: (file: File, keywords: string[]) => Promise<void>;
}
interface IGoogleItem {
	kind: string;
	title: string;
	link: string;
	displayLink: string;
	snippet: string;
	mime: string;
	fileFormat: string;
	image: {
		width: number;
		height: number;
		byteSize: number;
		thumbnailLink: string;
		thumbnailWidth: number;
		thumbnailHeight: number;
	};
}

interface IGoogleResponse {
	items: IGoogleItem[];
}

export const GoogleImagePanelV5 = (props: IGoogleImagePanelProps) => {
	const appState = useAppStore((state) => state.appState);
	const [searchValue, setSearchValue] = useState<string>(props.defaultSearchValue || "");
	const [searchResult, setSearchResult] = useState<IGoogleResponse | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [libraryItemsLength, setLibraryItemsLength] = useState<number>(() => props.addedItems.length);
	const timeoutId = useRef<number>(-1);

	useEffect(() => {
		if (searchValue) {
			timeoutId.current = window.setTimeout(async () => {
				setIsLoading(true);
				const googleResponse = await XHRLoader.loadAsync<IGoogleResponse>({
					url: "https://www.googleapis.com/customsearch/v1",
					method: XHRLoader.METHOD_GET,
					params: {
						rights: "cc_publicdomain,cc_attribute,cc_nonderived",
						q: searchValue,
						cx: "008962485971625166487:6yuvs5ridps",
						searchType: "image",
						key: "AIzaSyCXqaHGgfTWGoz--edjJxJa3mkyO4wY9tY",
					},
				});
				setIsLoading(false);
				setSearchResult(googleResponse);
			}, 500);
		} else {
			setSearchResult(null);
		}

		return () => clearTimeout(timeoutId.current);
	}, [searchValue]);

	const onAddItemToLibButtonClick = useCallback(
		async (item: IGoogleItem) => {
			setIsLoading(true);
			let img: HTMLImageElement;

			try {
				img = await ImageUtils.loadImage(item.link);
			} catch (error) {
				console.warn(`Image at the following link can't be downloaded due to CORS policy: ${item.link} So we're using its thumbnail now.`);
			} finally {
				if (!img) {
					img = await ImageUtils.loadImage(item.image.thumbnailLink);
				}
				const toJPG = item.mime !== "image/png";
				const base64 = await ImageUtils.image2RasterBase64String(appState.fonts, img, Infinity, toJPG ? "jpeg" : "png");
				const res = await fetch(base64);
				const blob = await res.blob();
				const file = new File([blob], item.title, {type: `image/${toJPG ? "jpeg" : "png"}`});

				await openKeywordsPopupForFile(file, "image", props.addImage, appState.currentUIVersion);
			}

			setIsLoading(false);
		},
		[appState.fonts, appState.currentUIVersion, props.addImage],
	);

	const result = useMemo(() => {
		return searchResult?.items?.map((item: IGoogleItem, index: number) => {
			return (
				<ResultImageContainer
					key={index}
					style={{backgroundImage: `url(${item.image.thumbnailLink})`}}
				>
					<ButtonV5
						label="Add to library"
						onClick={() => onAddItemToLibButtonClick(item)}
						disabled={isLoading}
					/>
				</ResultImageContainer>
			);
		});
	}, [searchResult, isLoading, onAddItemToLibButtonClick]);

	return (
		<Observer>
			{() => {
				return (
					<DomPortal destination={document.querySelector(".catalog-popup")}>
						<GoogleItemContainerStyled $disabled={isLoading}>
							<VerticalFlex
								$gap="8px"
								style={{minHeight: "100%"}}
							>
								<FlexCenter
									$gap="8px"
									style={{marginBottom: baseDistance.lg}}
								>
									<IconButtonV5
										IconComponent={GoBackIcon}
										title="Go back"
										className="marginLeftAuto"
										onClick={props.onCloseClick}
									/>
									<GoogleIcon />
									<h2>Google Image Search</h2>
								</FlexCenter>
								<div style={{width: 400}}>
									<SearchFieldV5
										placeholder="Search"
										value={searchValue}
										onInput={setSearchValue}
									/>
								</div>
								<ResultWrapper>{result}</ResultWrapper>
								<LibraryItemsContainer>
									<span>{Math.abs(libraryItemsLength - props.addedItems.length)} images added</span>
									<LibrabryItemsList>
										{props.addedItems.slice(libraryItemsLength, props.addedItems.length).map((item) => (
											<img
												height={32}
												src={item.thumbnail}
												key={item.id}
											/>
										))}
									</LibrabryItemsList>
								</LibraryItemsContainer>
							</VerticalFlex>
						</GoogleItemContainerStyled>
					</DomPortal>
				);
			}}
		</Observer>
	);
};

const LibrabryItemsList = styled.div`
	${FlexCenterStyle};
	gap: ${baseDistance.sm};
	min-height: "50px";
	padding: ${baseDistance.sm};
	border: solid 1px ${colorPalette.gray.c200Light};
	border-radius: ${baseDistance.sm};
	overflow-y: auto;
	min-height: 50px;
`;

const LibraryItemsContainer = styled.div`
	${VerticalFlexStyle};
	gap: ${baseDistance.sm};

	span {
		color: ${colorPalette.gray.c500Primary};
		font-size: ${baseDistance.oh};
		font-weight: ${fontWeight.bold};
	}
`;

const ResultWrapper = styled.div`
	flex: 1;
	display: grid;
	overflow: auto;
	grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
	grid-auto-rows: 100px;
	gap: ${baseDistance.sm};
	align-items: center;
`;

const GoogleItemContainerStyled = styled.div<{$disabled?: boolean}>`
	position: absolute;
	height: 100%;
	width: 100%;
	background-color: ${colorPalette.white};
	padding: ${baseDistance.md};
	border-radius: ${baseDistance.md};

	h2 {
		font-size: 24px;
		font-weight: ${fontWeight.bold};
	}
`;

const ResultImageContainer = styled.div`
	${FlexCenterStyle};
	align-items: center;
	justify-content: center;
	background-size: contain;
	height: 0;
	min-height: 100%;
	border-radius: ${baseDistance.xs};
	border: solid 2px transparent;

	${ButtonStyled} {
		display: none;
	}

	&:hover {
		border-color: ${colorPalette.primary.c500Primary};

		${ButtonStyled} {
			display: flex;
		}
	}
`;
